import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ScreenSearchDesktopOutlinedIcon from '@mui/icons-material/ScreenSearchDesktopOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import IconTile from '../../components/common/IconTile';
import SecondaryButton from '../../components/common/SecondaryButton';
import FaqAccordion from '../../components/tools/FaqAccordion';
import { getFaqs } from './faqConfig';
import { PRIMARY_COLORS, LIGHT_GRAY, WHITE } from '../../common/colors';
import { CHAT_URL } from '../../common/constants';
import { appLink } from '../../common/appLinks';
import PageMeta from '../../components/common/PageMeta';

// Webflow tools.html: layout387 toolkit cards + OCL Online panel,
// impact-stories CTA, FAQ accordion, "Still have questions?".
const CARD_ICONS = [ScreenSearchDesktopOutlinedIcon, ApiOutlinedIcon, AccountTreeOutlinedIcon];
const CARD_ROUTES = ['/tools/termbrowser', '/tools/terminology-service', '/tools/mapper'];

const getToolCards = t =>
  t('tools.cards', { returnObjects: true }).map((card, i) => ({ ...card, icon: CARD_ICONS[i], to: CARD_ROUTES[i] }));

const ToolCard = ({ icon, title, text, to }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ border: `1px solid ${LIGHT_GRAY}`, borderRadius: '12px', p: 4 }}>
      <IconTile icon={icon} sx={{ mb: 2 }} />
      <Typography variant="h5" component="h3" sx={{ color: 'surface.dark', mb: 1.5 }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: 'surface.contrastText' }}>
        {text}
      </Typography>
      <Button
        component={RouterLink}
        to={to}
        endIcon={<ArrowForwardIosIcon sx={{ fontSize: '12px !important' }} />}
        sx={{ color: 'primary.main', mt: 2, px: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
        {t('common.find_out_more')}
      </Button>
    </Box>
  );
};

const OclOnlinePanel = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ backgroundColor: PRIMARY_COLORS['10'], borderRadius: '12px', p: { xs: 4, md: 6 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 4, alignItems: 'center' }}>
      <IconTile icon={CloudOutlinedIcon} sx={{ width: 64, height: 64 }} />
      <Box>
        <Typography variant="body2" sx={{ color: PRIMARY_COLORS['80'], fontWeight: 600, mb: 0.5 }}>
          {t('tools.online_panel.eyebrow')}
        </Typography>
        <Typography variant="h4" component="h3" sx={{ color: WHITE, mb: 1.5 }}>
          {t('tools.online_panel.heading')}
        </Typography>
        <Typography sx={{ color: PRIMARY_COLORS['90'], mb: 3 }}>
          {t('tools.online_panel.text')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Button href={appLink('termBrowser')} sx={{ backgroundColor: WHITE, color: 'surface.dark', '&:hover': { backgroundColor: PRIMARY_COLORS['90'] } }}>
            {t('tools.online_panel.go_button')}
          </Button>
          <Button component={RouterLink} to="/tools/ocl-online" sx={{ border: `1px solid ${WHITE}`, color: WHITE, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            {t('common.find_out_more')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const Tools = () => {
  const { t } = useTranslation();
  return (
    <Box>
      <PageMeta title={t('tools.eyebrow')} description={t('tools.meta_description')} />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ textAlign: 'center', mx: 'auto', mb: { xs: 5, md: 7 } }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            {t('tools.eyebrow')}
          </Typography>
          <Typography variant="h2" component="h1" sx={{ color: 'surface.dark', fontSize: { xs: '2.25rem', md: '3rem' }, mb: 1.5 }}>
            {t('tools.heading')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText' }}>
            {t('tools.text')}
          </Typography>
        </Box>
        {/* docs/SEO-AUDIT.md §7.2 — visually-hidden heading so the H3 tool
            cards below don't skip straight from H1 to H3; the section has
            no visible heading of its own, `tools.heading` above already
            serves that role visually. */}
        <Typography variant="h2" component="h2" sx={visuallyHidden}>{t('tools.eyebrow')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4, mb: 4 }}>
          {getToolCards(t).map(card => <ToolCard key={card.title} {...card} />)}
        </Box>
        <OclOnlinePanel />
      </Container>
      <Box sx={{ backgroundColor: PRIMARY_COLORS['95'] }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 1.5 }}>
            {t('tools.impact_heading')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText', mb: 3 }}>
            {t('tools.impact_text')}
          </Typography>
          <SecondaryButton to="/blog?category=Community">{t('tools.impact_button')}</SecondaryButton>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 1 }}>
            {t('tools.faq_heading')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText' }}>
            {t('tools.faq_text')}
          </Typography>
        </Box>
        <FaqAccordion faqs={getFaqs(t)} />
        {/* These are the tool-relevant subset; /faq carries the full set. */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link component={RouterLink} to="/faq" sx={{ fontWeight: 600 }}>
            {t('tools.faq_see_all')}
          </Link>
        </Box>
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h4" component="h2" sx={{ color: 'surface.dark', mb: 1 }}>
            {t('tools.questions_heading')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText', mb: 3 }}>
            {t('tools.questions_text')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Button href={CHAT_URL} target="_blank" rel="noopener noreferrer" variant="contained" disableElevation>
              {t('tools.chat_button')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Tools;
